import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Send, Plus, Search, Calendar, ExternalLink, Mail } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const NoticesTab = ({ notices, meetings, searchTerm, setSearchTerm }) => {
  const navigate = useNavigate();
  const [noticeDialog, setNoticeDialog] = useState(false);
  const [noticeForm, setNoticeForm] = useState({ meeting_id: '' });

  const getMeetingTitle = (meetingId) => meetings.find(m => m.id === meetingId)?.title || 'Unknown Meeting';

  const handleNewNoticeNavigate = () => {
    if (!noticeForm.meeting_id) return;
    setNoticeDialog(false);
    navigate(`/meetings/${noticeForm.meeting_id}/notices`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6570]" />
          <Input placeholder="Search notices..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-[#E2E6EA]" data-testid="search-notices-hub" />
        </div>
        <Button onClick={() => { setNoticeForm({ meeting_id: '' }); setNoticeDialog(true); }}
          className="bg-[#00A3A3] hover:bg-[#008585] shrink-0" data-testid="new-notice-btn">
          <Plus className="h-4 w-4 mr-2" />New Notice
        </Button>
      </div>
      {notices.length === 0 ? (
        <Card className="border-[#E2E6EA]"><CardContent className="py-12 text-center">
          <Send className="h-10 w-10 mx-auto mb-3 text-[#A8B1B9]" />
          <h3 className="text-base font-medium text-[#1A1D21] mb-1">No notices sent yet</h3>
          <p className="text-sm text-[#5A6570]">Notices are created from meeting detail pages</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {notices.filter(n => (n.subject || '').toLowerCase().includes(searchTerm.toLowerCase()) || getMeetingTitle(n.meeting_id).toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0))
            .map((notice, idx) => (
            <Card key={notice.id || idx} className="border-[#E2E6EA] hover:border-[#00A3A3]/50 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-[#1A1D21] truncate">{notice.subject || 'Untitled Notice'}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-[#5A6570]">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{notice.sent_at ? format(parseISO(notice.sent_at), 'MMM d, yyyy h:mm a') : 'N/A'}</span>
                      {notice.meeting_id && <span className="flex items-center gap-1"><ExternalLink className="h-3 w-3" />{getMeetingTitle(notice.meeting_id)}</span>}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200"><Mail className="h-3 w-3 mr-1" />Sent</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* New Notice Dialog */}
      <Dialog open={noticeDialog} onOpenChange={setNoticeDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-[#00A3A3]" />Create New Notice</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-[#5A6570]">Select a meeting to create a notice for:</p>
            <Select value={noticeForm.meeting_id} onValueChange={v => setNoticeForm({ meeting_id: v })}>
              <SelectTrigger className="border-[#E2E6EA]" data-testid="notice-meeting-select"><SelectValue placeholder="Select a meeting..." /></SelectTrigger>
              <SelectContent>
                {meetings
                  .sort((a, b) => new Date(b.date_time || b.created_at || 0) - new Date(a.date_time || a.created_at || 0))
                  .map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.title} {m.date_time ? `(${format(parseISO(typeof m.date_time === 'string' ? m.date_time : new Date(m.date_time).toISOString()), 'MMM d, yyyy')})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {meetings.length === 0 && <p className="text-xs text-amber-600">No meetings found. Create a meeting first.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoticeDialog(false)}>Cancel</Button>
            <Button onClick={handleNewNoticeNavigate} disabled={!noticeForm.meeting_id} className="bg-[#00A3A3] hover:bg-[#008585]" data-testid="confirm-notice-meeting-btn">
              <ExternalLink className="h-4 w-4 mr-1.5" />Go to Notice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NoticesTab;
